import React, { useState, useEffect } from "react";
import { List, Grid, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "../../assets/css/event.css";
import { eventService } from "../../api/eventService";

// Image loader with skeleton
const ImageWithSkeleton = ({
    src,
    alt,
    height = 250,
    width = 250,
    className = "",
}) => {
    const [loaded, setLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        if (!src || src.trim() === "") {
            setHasError(true);
            return;
        }

        const img = new Image();
        img.src = src;
        img.onload = () => setTimeout(() => setLoaded(true), 150);
        img.onerror = () => setHasError(true);
    }, [src]);

    return (
        <div
            className="image-skeleton-wrapper"
            style={{
                position: "relative",
                width: typeof width === "number" ? `${width}px` : width,
                height: typeof height === "number" ? `${height}px` : height,
                overflow: "hidden",
                background: "#f9f9f9",
                flexShrink: 0,
            }}
        >
            {!loaded && !hasError && (
                <Skeleton
                    height="100%"
                    width="100%"
                    baseColor="#e0e0e0"
                    highlightColor="#f5f5f5"
                    style={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 1,
                    }}
                />
            )}

            {!hasError && (
                <img
                    src={src}
                    alt={alt}
                    loading="lazy"
                    className={className}
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        position: "absolute",
                        top: 0,
                        left: 0,
                        opacity: loaded ? 1 : 0,
                        transition: "opacity 0.5s ease-in-out",
                        zIndex: 2,
                    }}
                    onError={() => setHasError(true)}
                />
            )}

            {hasError && (
                <div
                    style={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#f0f0f0",
                        color: "#777",
                        fontSize: "14px",
                        fontWeight: "500",
                    }}
                >
                    No Image Found
                </div>
            )}
        </div>
    );
};

const EventsListSection = () => {
    const [view, setView] = useState("list");
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch events from API
    useEffect(() => {
        const fetchEvents = async () => {
            try {
                setLoading(true);
                const data = await eventService.getEvents();

                const eventList = data?.Event || [];
                const mappedEvents = eventList.map((e) => {
                    const startDate = new Date(e.EventStartDate);
                    const endDate = new Date(e.EventEndDate);

                    const month = startDate.toLocaleString("en-US", { month: "short" }).toUpperCase();
                    const day = String(startDate.getDate()).padStart(2, "0");
                    const year = startDate.getFullYear(); 


                    const formatFullDate = (date) =>
                        date.toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                        });

                    return {
                        id: e.Id,
                        date: { month, day, year }, 
                        startDate: formatFullDate(startDate),
                        endDate: formatFullDate(endDate),
                        image: e.EventImage || e.MediaBaseURL || "/assets/images/events/default.png",
                        title: e.EventName,
                        address: e.EventPlace,
                        time: `${e.EventStartTime?.slice(0, 5)} – ${e.EventEndTime?.slice(0, 5)}`,
                        description: e.EventShortDescription || e.EventLongDescription,
                        formUrl: e.FormURL || "",
                    };
                });

                setEvents(mappedEvents);
            } catch (err) {
                console.error("Error fetching events:", err);
                setError("Failed to load events. Please try again later.");
            } finally {
                setLoading(false);
            }
        };
        fetchEvents();
    }, []);

    const visibleEvents = events.slice(0);

    return (
        <div className="events-section">
            <div className="">
                {/* Header */}
                <div className="events-header" data-aos="fade-in">
                    <h6 className="events-title">Upcoming Events</h6>
                    <div className="view-toggle">
                        <div
                            className={`toggle-item ${view === "list" ? "active" : ""}`}
                            onClick={() => setView("list")}
                        >
                            <List size={18} strokeWidth={2} />
                            <span>LIST</span>
                        </div>
                        <div
                            className={`toggle-item ${view === "month" ? "active" : ""}`}
                            onClick={() => setView("month")}
                        >
                            <Grid size={18} strokeWidth={2} />
                            <span>MONTH</span>
                        </div>
                    </div>
                </div>

                {/* Loading or Error */}
                {loading && <p className="text-center mt-4">Loading events...</p>}
                {error && <p className="text-center text-danger mt-4">{error}</p>}

                {/* List View */}
                {!loading && !error && view === "list" && (
                    <div className="events-list">
                        {visibleEvents.map((event, idx) => (
                            <div className="event-item" key={event.id || idx}>
                                <div className="event-date">
                                    <span className="month">{event.date.month}</span>
                                    <span className="date-separator"></span>
                                    <span className="day">{event.date.day}</span>
                                    <span className="year">{event.date.year}</span> 

                                </div>
                                <ImageWithSkeleton
                                    src={event.image}
                                    alt={event.title}
                                    height={230}
                                    width={400}
                                    className="event-image"
                                />

                                <div className="event-content" data-aos="fade-left">
                                    <h5 className="event-title">{event.title}</h5>
                                    <p className="event-meta">
                                        {event.address} <br />
                                        {event.time} <br />

                                        {event.startDate} – {event.endDate}

                                    </p>
                                    <p className="event-desc">{event.description}</p>

                                    <div className="event-link-separator"></div>

                                    <Link
                                        to={event.formUrl || "#"}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="event-link"
                                    >
                                        View Event Details <ArrowRight size={15} strokeWidth={2} />
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Month/Grid View */}
                {!loading && !error && view === "month" && (
                    <div className="events-grid">
                        {visibleEvents.map((event, idx) => (
                            <div className="event-card" data-aos="fade-up" key={event.id || idx}>
                                <div className="event-date">
                                    <span className="month">{event.date.month}</span>
                                    <span className="date-separator"></span>
                                    <span className="day">{event.date.day}</span>
                                </div>
                                <ImageWithSkeleton
                                    src={event.image}
                                    alt={event.title}
                                    height={180}
                                    width="100%"
                                    className="event-image"
                                />
                                <div>
                                    <h5 className="event-title">{event.title}</h5>
                                    <p className="event-meta">{event.time}</p>
                                    {event.startDate} – {event.endDate}


                                    <Link
                                        to={event.formUrl || "#"}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="event-link grid-link"
                                    >
                                        View Event Details <ArrowRight size={15} strokeWidth={2} />
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default EventsListSection;
